'use client'

import { useEffect } from 'react'

function setThemeCookie(value: string) {
  document.cookie = `theme=${value};path=/;max-age=31536000;samesite=lax;secure`
}

/**
 * Syncs theme preference from localStorage/data-theme to a cookie.
 * The cookie is read server-side in layout.tsx to set data-theme on <html>
 * during SSR, preventing FOUC without needing an inline script.
 */
export default function ThemeProvider() {
  useEffect(() => {
    const root = document.documentElement

    // Determine initial theme: localStorage > dark (site default).
    // First-time visitors get dark regardless of OS preference; the
    // header toggle opts them into light, which then persists.
    const stored = localStorage.getItem('theme')
    if (stored) {
      root.setAttribute('data-theme', stored)
      setThemeCookie(stored)
    } else {
      root.setAttribute('data-theme', 'dark')
      localStorage.setItem('theme', 'dark')
      setThemeCookie('dark')
    }

    // Watch for data-theme changes (from toggles, ForceTheme, etc.)
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'data-theme') {
          const current = root.getAttribute('data-theme')
          if (current) {
            setThemeCookie(current)
          }
        }
      }
    })

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    return () => observer.disconnect()
  }, [])

  return null
}
